import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useLocationTracking } from '@/hooks/useLocationTracking';
import { toast } from '@/hooks/use-toast';

interface SOSHelper {
  id: string;
  sos_report_id: string;
  user_id: string;
  created_at: string;
  profiles?: {
    username: string;
    display_name: string | null;
    avatar_url: string | null;
  };
}

export const useSOSHelpers = (sosReportId: string | null) => {
  const { user } = useAuth();
  const { isSharing, enableLocationSharing } = useLocationTracking();
  const [isHelping, setIsHelping] = useState(false);
  const [helpers, setHelpers] = useState<SOSHelper[]>([]);
  const [loading, setLoading] = useState(false);

  // Check if user is helping and load helpers
  useEffect(() => {
    if (sosReportId) {
      checkIfHelping();
      loadHelpers();
      
      // Set up real-time subscription for helpers
      const channel = supabase
        .channel(`sos-helpers-${sosReportId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'sos_helpers',
            filter: `sos_report_id=eq.${sosReportId}`
          },
          () => {
            loadHelpers();
            if (user) checkIfHelping();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [sosReportId, user]);

  const checkIfHelping = async () => {
    if (!user || !sosReportId) return;
    
    try {
      const { data } = await supabase
        .from('sos_helpers')
        .select('id')
        .eq('sos_report_id', sosReportId)
        .eq('user_id', user.id)
        .single();
      
      setIsHelping(!!data);
    } catch (error) {
      setIsHelping(false);
    }
  };

  const loadHelpers = async () => {
    if (!sosReportId) return;
    
    try {
      // First get the helpers
      const { data: helpersData, error: helpersError } = await supabase
        .from('sos_helpers')
        .select('id, sos_report_id, user_id, created_at')
        .eq('sos_report_id', sosReportId)
        .order('created_at', { ascending: false });
      
      if (helpersError) throw helpersError;
      
      if (!helpersData || helpersData.length === 0) {
        setHelpers([]);
        return;
      }

      // Then get the profiles for these users
      const userIds = helpersData.map(h => h.user_id);
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, username, display_name, avatar_url')
        .in('user_id', userIds);
      
      if (profilesError) throw profilesError;

      // Combine the data
      const helpersWithProfiles = helpersData.map(helper => ({
        ...helper,
        profiles: profilesData?.find(p => p.user_id === helper.user_id)
      }));

      setHelpers(helpersWithProfiles || []);
    } catch (error) {
      console.error('Error loading helpers:', error);
    }
  };

  const toggleHelp = async () => {
    if (!user || !sosReportId) {
      toast({
        title: "Accesso richiesto",
        description: "Devi effettuare l'accesso per aiutare",
        variant: "destructive"
      });
      return;
    }

    try {
      setLoading(true);

      if (isHelping) {
        // Remove help
        const { error } = await supabase
          .from('sos_helpers')
          .delete()
          .eq('sos_report_id', sosReportId)
          .eq('user_id', user.id);

        if (error) throw error;

        // Update local state immediately for better UX
        setIsHelping(false);
        
        toast({
          title: "Non stai più aiutando",
          description: "Ti sei rimosso dalla lista degli aiutanti"
        });
      } else {
        // Add help
        const { error } = await supabase
          .from('sos_helpers')
          .insert({
            sos_report_id: sosReportId,
            user_id: user.id
          });

        if (error) throw error;

        // Update local state immediately for better UX
        setIsHelping(true);

        toast({
          title: "Grazie per l'aiuto!",
          description: isSharing 
            ? "Ti sei unito alla lista degli aiutanti" 
            : "Ti sei unito agli aiutanti. Considera di attivare la condivisione posizione per coordinare meglio le ricerche"
        });
      }
      
      // Reload helpers list to sync the count
      setTimeout(() => {
        loadHelpers();
      }, 100);
    } catch (error) {
      console.error('Error toggling help:', error);
      toast({
        title: "Errore",
        description: "Impossibile modificare lo stato di aiuto. Riprova.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    isHelping,
    helpers,
    loading,
    toggleHelp,
    helperCount: helpers.length
  };
};